import React, { useState, useRef, useEffect } from 'react';
import PlacesAutocompleteInput from './PlacesAutocompleteInput';
import AdditionalDestinationsInput from './AdditionalDestinationsInput';
import GoogleMapsComponent from './GoogleMapsComponent';
import CountrySelector from './CountrySelector';
import { MapLocation, RouteInfo } from '../types';
import { MapPin, Navigation, Route, Map, Info, RefreshCw, HelpCircle, ChevronDown, ChevronUp, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface QuotationFormLocationSectionProps {
  origin: { address: string; location: MapLocation | null };
  destination: { address: string; location: MapLocation | null };
  additionalDestinations: { address: string; location: MapLocation | null; loadStatus?: 'loaded' | 'empty' }[];
  setOrigin: React.Dispatch<React.SetStateAction<{ address: string; location: MapLocation | null }>>;
  setDestination: React.Dispatch<React.SetStateAction<{ address: string; location: MapLocation | null }>>;
  setAdditionalDestinations: React.Dispatch<React.SetStateAction<{ address: string; location: MapLocation | null; loadStatus?: 'loaded' | 'empty' }[]>>;
  onRouteCalculated: (routeInfo: RouteInfo) => void;
  calculatingRoute: boolean;
  calculatedRoute: boolean;
  routeInfo?: RouteInfo;
  firstSegmentLoadStatus?: 'loaded' | 'empty';
  setFirstSegmentLoadStatus?: (status: 'loaded' | 'empty') => void;
  showAdditionalDestinations: boolean;
  setShowAdditionalDestinations: (show: boolean) => void;
}

const QuotationFormLocationSection: React.FC<QuotationFormLocationSectionProps> = ({
  origin,
  destination,
  additionalDestinations,
  setOrigin,
  setDestination,
  setAdditionalDestinations,
  onRouteCalculated,
  calculatingRoute,
  calculatedRoute,
  routeInfo,
  firstSegmentLoadStatus = 'loaded',
  setFirstSegmentLoadStatus,
  showAdditionalDestinations,
  setShowAdditionalDestinations
}) => {
  const { t } = useLanguage();
  const mapRef = useRef<any>(null);
  const [showMapInstructions, setShowMapInstructions] = useState<boolean>(false);
  const [originCountry, setOriginCountry] = useState<string | null>('MX'); // Default to Mexico
  const [destinationCountry, setDestinationCountry] = useState<string | null>('MX'); // Default to Mexico
  const [additionalDestinationsCountry, setAdditionalDestinationsCountry] = useState<string | null>('MX'); // Default to Mexico

  // Handle origin country change
  const handleOriginCountryChange = (countryCode: string | null) => {
    console.log('Origin country changed to:', countryCode);
    setOriginCountry(countryCode);
    
    // Clear origin location when country changes to avoid confusion
    setOrigin({ address: '', location: null });
    
    // Clear route info if origin was previously set
    if (origin.location) {
      onRouteCalculated({
        origin: null,
        destination: destination.location,
        distance: 0,
        duration: '',
        originAddress: '',
        destinationAddress: destination.address
      });
    }
  };

  // Handle destination country change  
  const handleDestinationCountryChange = (countryCode: string | null) => {
    console.log('Destination country changed to:', countryCode);
    setDestinationCountry(countryCode);
    
    // Clear destination location when country changes to avoid confusion
    setDestination({ address: '', location: null });
    
    // Clear route info if destination was previously set
    if (destination.location) {
      onRouteCalculated({
        origin: origin.location,
        destination: null,
        distance: 0,
        duration: '',
        originAddress: origin.address,
        destinationAddress: ''
      });
    }
  };

  // Handle additional destinations country change
  const handleAdditionalDestinationsCountryChange = (countryCode: string | null) => {
    console.log('Additional destinations country changed to:', countryCode);
    setAdditionalDestinationsCountry(countryCode);
    
    // Clear all additional destinations when country changes to avoid confusion
    setAdditionalDestinations([]);
    
    // Clear route info if additional destinations were set
    onRouteCalculated({
      origin: origin.location,
      destination: destination.location,
      distance: 0,
      duration: '',
      originAddress: origin.address,
      destinationAddress: destination.address
    });
  };

  const handleOriginChange = (address: string) => {
    setOrigin({ ...origin, address });
  };

  const handleDestinationChange = (address: string) => {
    setDestination({ ...destination, address });
  };

  const handleOriginPlaceSelected = (place: google.maps.places.PlaceResult) => {
    if (place.geometry && place.geometry.location) {
      console.log('Origin place selected:', place.formatted_address);
      console.log('Origin coordinates:', place.geometry.location.lat(), place.geometry.location.lng());
      
      setOrigin({
        address: place.formatted_address || origin.address,
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }
      });
      
      // Ajustar el mapa a los marcadores después de seleccionar el origen
      setTimeout(() => {
        if (mapRef.current && typeof mapRef.current.fitMapToMarkers === 'function') {
          mapRef.current.fitMapToMarkers();
        }
      }, 300);
    } else {
      console.warn('Origin place selected but no geometry found:', place);
    }
  };

  const handleDestinationPlaceSelected = (place: google.maps.places.PlaceResult) => {
    if (place.geometry && place.geometry.location) {
      console.log('Destination place selected:', place.formatted_address);
      console.log('Destination coordinates:', place.geometry.location.lat(), place.geometry.location.lng());
      
      setDestination({
        address: place.formatted_address || destination.address,
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }
      });
      
      // Ajustar el mapa a los marcadores después de seleccionar el destino
      setTimeout(() => {
        if (mapRef.current && typeof mapRef.current.fitMapToMarkers === 'function') {
          mapRef.current.fitMapToMarkers();
        }
      }, 300);
    } else {
      console.warn('Destination place selected but no geometry found:', place);
    }
  };

  // Handle coordinate updates from the GoogleMapsComponent when markers are dragged
  const handleMapRouteInfo = (routeInfo: RouteInfo) => {
    console.log("Route info from map:", routeInfo);
    
    // Update origin location if it has changed (from dragging marker)
    if (routeInfo.originAddress?.startsWith('Lat:') && 
        routeInfo.origin && 
        origin.location?.lat !== routeInfo.origin.lat) {
      setOrigin({
        address: routeInfo.originAddress,
        location: routeInfo.origin
      });
    }
    
    // Update destination location if it has changed (from dragging marker)
    if (routeInfo.destinationAddress?.startsWith('Lat:') && 
        routeInfo.destination && 
        destination.location?.lat !== routeInfo.destination.lat) {
      setDestination({
        address: routeInfo.destinationAddress,
        location: routeInfo.destination
      });
    }
    
    // Pass along route info to parent
    if (routeInfo.distance > 0) {
      onRouteCalculated(routeInfo);
    }
  };

  // Effect to trigger map marker creation when locations change
  useEffect(() => {
    // If we have both origin and destination coordinates, request map update
    if (origin.location && destination.location && mapRef.current) {
      console.log('Both origin and destination coordinates available, map should update markers');
    }
  }, [origin.location, destination.location]);

  // Reset function to clear form and map
  const handleReset = () => {
    console.log("Resetting map and location data");
    
    // Reset form inputs
    setOrigin({ address: '', location: null });
    setDestination({ address: '', location: null });
    setAdditionalDestinations([]);
    
    // Reset load status if available
    if (setFirstSegmentLoadStatus) {
      setFirstSegmentLoadStatus('loaded');
    }
    
    // Reset countries to default
    setOriginCountry('MX');
    setDestinationCountry('MX');
    setAdditionalDestinationsCountry('MX');
    
    // Clear route info by passing empty route
    onRouteCalculated({
      origin: null,
      destination: null,
      distance: 0,
      duration: '',
      originAddress: '',
      destinationAddress: ''
    });
    
    // Force map reload - we do this by triggering the map component's state reset
    if (mapRef.current && typeof mapRef.current.resetMap === 'function') {
      console.log("Calling resetMap function on map reference");
      mapRef.current.resetMap();
    } else {
      console.warn("Map reference or resetMap method is not available");
    }
  };

  // Calculate route directly - this will call the map's calculateRoute method
  const handleCalculateRoute = () => {
    // Verify we have origin and destination
    if (!origin.location || !destination.location) {
      console.warn("Cannot calculate route: missing origin or destination");
      return;
    }
    
    console.log("Calculating route between:", origin.address, "and", destination.address);
    
    if (mapRef.current && typeof mapRef.current.calculateRoute === 'function') {
      mapRef.current.calculateRoute();
      
      // Ajustar mapa a los marcadores después de calcular la ruta
      setTimeout(() => {
        if (mapRef.current && typeof mapRef.current.fitMapToMarkers === 'function') {
          mapRef.current.fitMapToMarkers();
        }
      }, 500); // Mayor retraso para dar tiempo a que la ruta se calcule
    } else {
      console.warn("Map reference or calculateRoute method not available");
    }
  };

  // Check if we have enough data to calculate a route
  const canCalculateRoute = origin.location && destination.location;

  return (
    <div className="mb-6">
      {/* Enhanced Transport Route Header */}
      <div className="mb-4 p-4 bg-gradient-to-r from-primary-light to-primary-dark rounded-lg text-white shadow-lg">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center">
          <Route size={24} className="mr-3" />
          {t('quotationForm.transportRoute')}
        </h2>
        <p className="text-white/80 text-sm mt-1">
          {t('quotationForm.transportRouteDescription')}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          {/* Origin Country Selection */}
          <div className="mb-2">
            <label className="flex items-center mb-1 text-xs font-medium text-gray-600">
              {t('locationSelection.originCountryLabel')}
              <div className="relative inline-block ml-1 group">
                <HelpCircle size={12} className="text-blue-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-48 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('locationSelection.originCountryHelper')}
                </div>
              </div>
            </label>
            <CountrySelector
              selectedCountry={originCountry}
              onCountryChange={handleOriginCountryChange}
              allowGlobal={true}
              placeholder="País del origen"
              className="text-sm"
            />
          </div>
          
          <div className="mb-1">
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('quotationForm.origin')}
              </label>
              <div className="relative inline-block ml-2 group">
                <HelpCircle size={14} className="text-blue-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('locationSelection.locationInputHelper')}
                </div>
              </div>
            </div>
          </div>
          <PlacesAutocompleteInput
            id="origin-location-input"
            value={origin.address}
            onChange={handleOriginChange}
            onPlaceSelected={handleOriginPlaceSelected}
            placeholder={t('quotationForm.enterOrigin')}
            countryCode={originCountry}
            required
          />
          {origin.location && (
            <div className="text-xs text-green-600 mt-1 flex items-center">
              <Map size={12} className="mr-1" />
              <span>{t('mapComponent.locationConfirmed')}: ({origin.location.lat.toFixed(4)}, {origin.location.lng.toFixed(4)})</span>
            </div>
          )}
        </div>
        
        <div>
          {/* Destination Country Selection */}
          <div className="mb-2">
            <label className="flex items-center mb-1 text-xs font-medium text-gray-600">
              {t('locationSelection.destinationCountryLabel')}
              <div className="relative inline-block ml-1 group">
                <HelpCircle size={12} className="text-blue-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-48 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('locationSelection.destinationCountryHelper')}
                </div>
              </div>
            </label>
            <CountrySelector
              selectedCountry={destinationCountry}
              onCountryChange={handleDestinationCountryChange}
              allowGlobal={true}
              placeholder="País del destino"
              className="text-sm"
            />
          </div>
          
          <div className="mb-1">
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('quotationForm.destination')}
              </label>
              <div className="relative inline-block ml-2 group">
                <HelpCircle size={14} className="text-blue-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('locationSelection.locationInputHelper')}
                </div>
              </div>
            </div>
          </div>
          <PlacesAutocompleteInput
            id="destination-location-input"
            value={destination.address}
            onChange={handleDestinationChange}
            onPlaceSelected={handleDestinationPlaceSelected}
            placeholder={t('quotationForm.enterDestination')}
            countryCode={destinationCountry}
            required
          />
          {destination.location && (
            <div className="text-xs text-green-600 mt-1 flex items-center">
              <Map size={12} className="mr-1" />
              <span>{t('mapComponent.locationConfirmed')}: ({destination.location.lat.toFixed(4)}, {destination.location.lng.toFixed(4)})</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Additional Destinations Checkbox */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showAdditionalDestinations}
            onChange={(e) => setShowAdditionalDestinations(e.target.checked)}
            className="sr-only"
          />
          <div className={`w-4 h-4 border-2 rounded-sm mr-2 flex items-center justify-center ${
            showAdditionalDestinations ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
          }`}>
            {showAdditionalDestinations && <CheckCircle size={12} className="text-white" />}
          </div>
          <span className="text-sm font-medium text-blue-800 flex items-center">
            <MapPin size={14} className="mr-1.5" />
            {t('quotationForm.enableAdditionalDestinations')}
          </span>
        </label>
        <p className="text-xs text-blue-700 mt-1 ml-6">
          {t('quotationForm.additionalDestinationsDescription')}
        </p>
      </div>
      
      {/* Additional Destinations Section */}
      {showAdditionalDestinations && (
        <div>
          {/* Country Selection for Additional Destinations */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <label className="block text-xs font-medium text-gray-600">
                {t('quotation.additionalDestinationsCountry')}
              </label>
              <div className="relative inline-block ml-1 group">
                <HelpCircle size={12} className="text-blue-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-48 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('quotation.additionalDestinationsCountryHelper')}
                </div>
              </div>
            </div>
            <CountrySelector
              selectedCountry={additionalDestinationsCountry}
              onCountryChange={handleAdditionalDestinationsCountryChange}
              allowGlobal={true}
              placeholder={t('quotation.additionalDestinationsCountry')}
              className="text-sm"
            />
            
            {/* Show international route indicator if countries differ */}
            {(originCountry !== additionalDestinationsCountry || destinationCountry !== additionalDestinationsCountry) && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-center text-amber-800 text-xs">
                  <Info size={12} className="mr-1.5 flex-shrink-0" />
                  {t('quotation.internationalRoute')}: {t('quotation.internationalRouteHelper')}
                </div>
              </div>
            )}
          </div>
          
          <AdditionalDestinationsInput 
            additionalDestinations={additionalDestinations}
            setAdditionalDestinations={setAdditionalDestinations}
            maxDestinations={5}
            firstSegmentLoadStatus={firstSegmentLoadStatus}
            setFirstSegmentLoadStatus={setFirstSegmentLoadStatus}
            countryCode={additionalDestinationsCountry}
          />
        </div>
      )}
      
      <div className="mt-4 mb-6 border border-blue-200 rounded-lg overflow-hidden">
        {/* Collapsible Header */}
        <div 
          className="bg-blue-100 p-3 flex items-center justify-between cursor-pointer hover:bg-blue-200 transition-colors"
          onClick={() => setShowMapInstructions(!showMapInstructions)}
        >
          <div className="flex items-center">
            <Info size={18} className="text-blue-600 mr-2" />
            <h4 className="font-medium text-blue-800">{t('mapComponent.instructions')}</h4>
          </div>
          <button className="text-blue-600 p-1 hover:bg-blue-300 rounded-full transition-colors">
            {showMapInstructions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
        
        {/* Collapsible Content */}
        {showMapInstructions && (
          <div className="bg-blue-50 p-4 text-blue-700 text-sm">
            <div className="flex items-start">
              <div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>{t('locationSelection.option1')}</li>
                  <li>{t('mapComponent.instructionsDragMarkers')}</li>
                  <li>{t('mapComponent.instructionsMap')}</li>
                  <li>{t('locationSelection.checkMap')}</li>
                </ul>
                
                <p className="mt-3 text-blue-800 font-semibold">{t('locationSelection.routeOrder')}</p>
                <p className="text-blue-600">{t('locationSelection.routeSequence')}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Button to calculate route */}
      {canCalculateRoute && (
        <div className="my-5 flex justify-center">
          <button
            type="button"
            onClick={handleCalculateRoute}
            disabled={calculatingRoute}
            className="py-3 px-8 bg-primary text-white font-semibold text-lg rounded-lg shadow-lg hover:bg-primary-dark transition-colors flex items-center"
          >
            {calculatingRoute ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                {t('quotationForm.calculating')}
              </>
            ) : (
              <>
                <Navigation size={20} className="mr-3" />
                {t('mapComponent.calculateRouteNow')}
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Map Container */}
      <div className="mt-2 relative">
        <GoogleMapsComponent
          ref={mapRef}
          origin={origin.location}
          destination={destination.location}
          additionalDestinations={additionalDestinations.map(d => d.location).filter((loc): loc is MapLocation => !!loc)}
          additionalDestinationAddresses={additionalDestinations.map(d => d.address)}
          originAddress={origin.address}
          destinationAddress={destination.address}
          onRouteCalculated={handleMapRouteInfo}
          style={{ width: '100%', height: '550px' }}
        />
        
        {/* Map controls */}
        <div className="absolute top-3 left-3 z-50 flex flex-col gap-2">
          {/* Reset Map Button */}
          <button 
            type="button" 
            onClick={handleReset}
            className="px-3 py-2 bg-white text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium shadow-md flex items-center"
          >
            <RefreshCw size={16} className="mr-1.5" />
            {t('mapComponent.clearMap')}
          </button>
        </div>
      </div>
      
      {/* Route Information Display */}
      {calculatingRoute && (
        <div className="mt-4 text-center text-primary">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mx-auto mb-2"></div>
          {t('quotationForm.calculating')}
        </div>
      )}
      
      {calculatedRoute && routeInfo && (
        <div className="mt-4 bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-800 mb-2 flex items-center">
            <Navigation className="mr-2" size={18} />
            {t('quotationForm.routeInfo')}
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="flex items-start text-gray-700">
                <MapPin size={16} className="mr-1.5 text-gray-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-medium block mb-1">{t('quotationForm.locationsSelected')}</span>
                  {origin.address && destination.address ? (
                    <span className="text-sm">
                      {origin.address.split(',')[0]} → {destination.address.split(',')[0]}
                      {additionalDestinations.length > 0 && additionalDestinations.map((dest, index) => (
                        dest.address && (
                          <span key={index}> → {dest.address.split(',')[0]}</span>
                        )
                      ))}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500 italic">Select locations</span>
                  )}
                </span>
              </p>
            </div>
            
            <div className="space-y-1">
              <p className="text-gray-700">
                <span className="font-medium">{t('quotationForm.distance')}</span> {routeInfo.distance.toFixed(2)} km ({t('quotationForm.route.oneWay')})
              </p>
              <p className="text-gray-700">
                <span className="font-medium">{t('quotationForm.estTime')}</span> {routeInfo.duration}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">{t('quotationForm.totalDistance')}</span> {(routeInfo.distance * 2).toFixed(2)} km ({t('quotationForm.route.roundTrip')})
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationFormLocationSection;